export type ApiError = { message: string; code: number };
type HttpFailure = { code?: string; response?: { status: number; data?: unknown } };

/** Proxy HTML is a transport failure, never a message to show inside the app. */
export function toApiError(error: HttpFailure): ApiError {
  const code = error.response?.status ?? 0;
  if (code === 504 || ['ECONNABORTED', 'ETIMEDOUT'].includes(error.code ?? '')) {
    return { code: code || 504, message: 'The server took too long to reply. Your request may still be processing. Refresh before trying again.' };
  }
  if (!error.response) return { code, message: 'Could not reach the server. Check your connection, then refresh and try again.' };
  const data = error.response.data;
  if ((code === 502 || code === 503) && (!data || typeof data !== 'object')) {
    return { code, message: 'The service is temporarily unavailable. Please try again shortly.' };
  }
  let message = 'Something went wrong. Please refresh and try again.';
  if (typeof data === 'string') message = data;
  else if (data && typeof data === 'object') {
    const body = data as { detail?: unknown; message?: unknown };
    if (typeof body.detail === 'string') message = body.detail;
    else if (Array.isArray(body.detail)) {
      message = body.detail.slice(0, 8).map((raw: unknown) => {
        if (!raw || typeof raw !== 'object') return 'Invalid value';
        const item = raw as { loc?: unknown[]; msg?: unknown };
        return `${Array.isArray(item.loc) ? item.loc[item.loc.length - 1] ?? '' : ''} ${typeof item.msg === 'string' ? item.msg : 'Invalid value'}`.trim();
      }).join(', ');
    } else if (body.detail && typeof body.detail === 'object') {
      const detail = body.detail as { message?: unknown; rows?: unknown };
      if (typeof detail.message === 'string') message = [detail.message, ...(Array.isArray(detail.rows) ? detail.rows.filter((x) => typeof x === 'string').slice(0, 8) : [])].join(' ');
    } else if (typeof body.message === 'string') message = body.message;
  }
  if (!message.trim() || /<!doctype|<\/?[a-z][^>]*>/i.test(message)) {
    message = 'The server returned an unexpected response. Please refresh and try again.';
  }
  return { code, message: message.slice(0, 700) };
}
